import {useState, useEffect} from 'react';
import {Clock} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({length: 24}, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`
}));

export type Preset = 'onDemand' | 'interval' | 'daily' | 'weekly' | 'custom';
export type IntervalUnit = 'minutes' | 'hours';

const MAX_BY_UNIT: Record<IntervalUnit, number> = {minutes: 59, hours: 23};

const ALL_PRESETS: Array<{id: Preset; label: string}> = [
  {id: 'onDemand', label: 'On demand'},
  {id: 'interval', label: 'Every N minutes'},
  {id: 'daily', label: 'Daily'},
  {id: 'weekly', label: 'Weekly'},
  {id: 'custom', label: 'Custom'}
];

function clampInterval(value: number, unit: IntervalUnit): number {
  return Math.min(Math.max(1, Math.trunc(value) || 1), MAX_BY_UNIT[unit]);
}

export type ScheduleFields = {
  hour: number;
  day: number;
  custom: string;
  intervalValue: number;
  intervalUnit: IntervalUnit;
};

export function buildCron(preset: Preset, fields: ScheduleFields): string | null {
  const {hour, day, custom, intervalValue, intervalUnit} = fields;
  if (preset === 'onDemand') {
    return null;
  }

  if (preset === 'interval') {
    const n = clampInterval(intervalValue, intervalUnit);
    return intervalUnit === 'minutes' ? `*/${n} * * * *` : `0 */${n} * * *`;
  }

  if (preset === 'daily') {
    return `0 ${hour} * * *`;
  }

  if (preset === 'weekly') {
    return `0 ${hour} * * ${day}`;
  }

  return custom;
}

export function describeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) {
    return parts.length > 1 ? 'Incomplete expression' : '—';
  }

  const [min, hr, dom, month, dow] = parts as [string, string, string, string, string];

  const isWild = (p: string) => p === '*';
  const isNumber = (p: string) => /^\d+$/.test(p);
  const isStep = (p: string) => /^\*\/\d+$/.test(p);
  const pad = (n: string) => n.padStart(2, '0');
  const time = (h: string, m: string) => `${pad(h)}:${pad(m)} UTC`;
  const ordinal = (n: number) => `${n}${[, 'st', 'nd', 'rd'][n] ?? 'th'}`;

  const dayName = (d: string) => DAYS[Number.parseInt(d)] ?? `day ${d}`;

  if ([min, hr, dom, month, dow].every(isWild)) {
    return 'Every minute';
  }

  if (isStep(min) && [hr, dom, month, dow].every(isWild)) {
    const n = min.split('/')[1];
    return n === '1' ? 'Every minute' : `Every ${n} minutes`;
  }

  if (isNumber(min) && [hr, dom, month, dow].every(isWild)) {
    const m = Number.parseInt(min);
    if (m === 0) {
      return 'Every hour';
    }

    return `At ${m} minute${m === 1 ? '' : 's'} past every hour`;
  }

  if (isNumber(min) && isStep(hr) && [dom, month, dow].every(isWild)) {
    const n = hr.split('/')[1];
    return `Every ${n} hours` + (min === '0' ? '' : ` at :${pad(min)}`);
  }

  if (isNumber(min) && isNumber(hr) && [dom, month, dow].every(isWild)) {
    return `Every day at ${time(hr, min)}`;
  }

  if (isNumber(min) && isNumber(hr) && isWild(dom) && isWild(month) && isNumber(dow)) {
    return `Every ${dayName(dow)} at ${time(hr, min)}`;
  }

  if (isNumber(min) && isNumber(hr) && isWild(dom) && isWild(month) && dow === '1-5') {
    return `Weekdays (Mon–Fri) at ${time(hr, min)}`;
  }

  if (
    isNumber(min) &&
    isNumber(hr) &&
    isWild(dom) &&
    isWild(month) &&
    (dow === '6,0' || dow === '0,6')
  ) {
    return `Weekends at ${time(hr, min)}`;
  }

  if (isNumber(min) && isNumber(hr) && isWild(dom) && isWild(month) && dow.includes(',')) {
    const names = dow.split(',').map(dayName);
    const last = names.pop()!;
    return `Every ${names.length > 0 ? names.join(', ') + ' and ' + last : last} at ${time(hr, min)}`;
  }

  if (isNumber(min) && isNumber(hr) && isWild(dom) && isWild(month) && /^\d-\d$/.test(dow)) {
    const [from, to] = dow.split('-') as [string, string];
    return `Every ${dayName(from)} to ${dayName(to)} at ${time(hr, min)}`;
  }

  if (isNumber(min) && isNumber(hr) && isNumber(dom) && isWild(month) && isWild(dow)) {
    return `On the ${ordinal(Number.parseInt(dom))} of every month at ${time(hr, min)}`;
  }

  const MONTHS = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  if (isNumber(min) && isNumber(hr) && isWild(dom) && isNumber(month) && isWild(dow)) {
    return `Every day in ${MONTHS[Number.parseInt(month)] ?? `month ${month}`} at ${time(hr, min)}`;
  }

  return 'Custom schedule';
}

export function describeSchedule(preset: Preset, fields: ScheduleFields): string {
  const {hour, day, custom, intervalValue, intervalUnit} = fields;
  if (preset === 'onDemand') {
    return 'Run on demand only';
  }

  if (preset === 'interval') {
    const n = clampInterval(intervalValue, intervalUnit);
    const unitLabel = intervalUnit === 'minutes' ? 'minute' : 'hour';
    return `Every ${n} ${unitLabel}${n === 1 ? '' : 's'}`;
  }

  if (preset === 'daily') {
    return `Every day at ${HOURS[hour]?.label ?? ''} UTC`;
  }

  if (preset === 'weekly') {
    return `Every ${DAYS[day] ?? ''} at ${HOURS[hour]?.label ?? ''} UTC`;
  }

  return describeCron(custom);
}

export function parseCron(expr: string | null): {
  preset: Preset;
  hour: number;
  day: number;
  custom: string;
  intervalValue: number;
  intervalUnit: IntervalUnit;
} {
  if (expr === null || expr.trim() === '') {
    return {
      preset: 'onDemand',
      hour: 9,
      day: 1,
      custom: '',
      intervalValue: 15,
      intervalUnit: 'minutes'
    };
  }

  const everyMinutes = /^\*\/(\d+)(?: \*){4}$/.exec(expr);
  if (everyMinutes) {
    return {
      preset: 'interval',
      hour: 9,
      day: 1,
      custom: '',
      intervalValue: Number.parseInt(everyMinutes[1] ?? '15'),
      intervalUnit: 'minutes'
    };
  }

  const everyHours = /^0 \*\/(\d+) \* \* \*$/.exec(expr);
  if (everyHours) {
    return {
      preset: 'interval',
      hour: 9,
      day: 1,
      custom: '',
      intervalValue: Number.parseInt(everyHours[1] ?? '1'),
      intervalUnit: 'hours'
    };
  }

  if (expr === '0 * * * *') {
    return {
      preset: 'interval',
      hour: 9,
      day: 1,
      custom: '',
      intervalValue: 1,
      intervalUnit: 'hours'
    };
  }

  const daily = /^0 (\d+) \* \* \*$/.exec(expr);
  if (daily) {
    return {
      preset: 'daily',
      hour: Number.parseInt(daily[1] ?? '0'),
      day: 1,
      custom: '',
      intervalValue: 15,
      intervalUnit: 'minutes'
    };
  }

  const weekly = /^0 (\d+) \* \* (\d+)$/.exec(expr);
  if (weekly) {
    return {
      preset: 'weekly',
      hour: Number.parseInt(weekly[1] ?? '0'),
      day: Number.parseInt(weekly[2] ?? '0'),
      custom: '',
      intervalValue: 15,
      intervalUnit: 'minutes'
    };
  }

  return {
    preset: 'custom',
    hour: 9,
    day: 1,
    custom: expr,
    intervalValue: 15,
    intervalUnit: 'minutes'
  };
}

type Props = {
  /** Cron expression, or null for "no schedule" (only meaningful when `allowOnDemand` is set). */
  readonly value: string | null;
  readonly onChange: (cron: string | null) => void;
  /** Show the "On demand" preset tile and allow `value`/`onChange` to be null. Default false. */
  readonly allowOnDemand?: boolean;
  /** Denser sizing to match tighter host forms (e.g. the Collection modal). Default false. */
  readonly compact?: boolean;
  /** Show the "Every N minutes" preset. Default true. A stored interval opens as Daily without it. */
  readonly allowInterval?: boolean;
};

/** Friendly preset-based cron builder — interval/Daily/Weekly/Custom, converted to a cron string under the hood. */
export function ScheduleInput({
  value,
  onChange,
  allowOnDemand = false,
  compact = false,
  allowInterval = true
}: Props) {
  const parsed = parseCron(value);
  const [preset, setPreset] = useState<Preset>(
    parsed.preset === 'interval' && !allowInterval ? 'daily' : parsed.preset
  );
  const [hour, setHour] = useState(parsed.hour);
  const [day, setDay] = useState(parsed.day);
  const [custom, setCustom] = useState(parsed.custom);
  const [intervalValue, setIntervalValue] = useState(parsed.intervalValue);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>(parsed.intervalUnit);

  const fields: ScheduleFields = {hour, day, custom, intervalValue, intervalUnit};
  const cron = buildCron(preset, fields);
  const description = describeSchedule(preset, fields);

  useEffect(() => {
    onChange(cron);
    // Only push changes outward when the user edits a control here, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, hour, day, custom, intervalValue, intervalUnit]);

  const presets = ALL_PRESETS.filter(
    (p) => (allowOnDemand || p.id !== 'onDemand') && (allowInterval || p.id !== 'interval')
  );

  // `compact` used to pick a denser set of hand-rolled sizes; the design system's controls are one
  // size, so it now only decides whether the summary is a one-line note or a full Alert.
  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      <Field>
        <FieldLabel>Execution Cadence</FieldLabel>
        <ToggleGroup
          variant="outline"
          spacing={0}
          size="sm"
          className="flex-wrap"
          value={[preset]}
          onValueChange={(next) => {
            const [chosen] = next;
            if (chosen && presets.some((p) => p.id === chosen)) {
              setPreset(chosen as Preset);
            }
          }}>
          {presets.map((p) => (
            <ToggleGroupItem key={p.id} value={p.id}>
              {p.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>

      {preset === 'interval' ? (
        <div className="flex items-end gap-3">
          <Field className="w-24">
            <FieldLabel htmlFor="schedule-interval-value">Every</FieldLabel>
            <Input
              id="schedule-interval-value"
              type="number"
              min={1}
              max={MAX_BY_UNIT[intervalUnit]}
              value={intervalValue}
              onChange={(e) => {
                setIntervalValue(Number(e.target.value));
              }}
            />
          </Field>
          <Field className="w-fit">
            <FieldLabel htmlFor="schedule-interval-unit">Unit</FieldLabel>
            <NativeSelect
              id="schedule-interval-unit"
              value={intervalUnit}
              onChange={(e) => {
                const unit = e.target.value as IntervalUnit;
                setIntervalUnit(unit);
                setIntervalValue((v) => clampInterval(v, unit));
              }}>
              <NativeSelectOption value="minutes">Minutes</NativeSelectOption>
              <NativeSelectOption value="hours">Hours</NativeSelectOption>
            </NativeSelect>
          </Field>
        </div>
      ) : null}

      {preset === 'daily' || preset === 'weekly' ? (
        <div className="grid grid-cols-2 gap-3">
          {preset === 'weekly' ? (
            <Field>
              <FieldLabel htmlFor="schedule-day">Day of Week</FieldLabel>
              <NativeSelect
                id="schedule-day"
                wrapperClassName="w-full"
                value={day}
                onChange={(e) => {
                  setDay(Number(e.target.value));
                }}>
                {DAYS.map((d, i) => (
                  <NativeSelectOption key={d} value={i}>
                    {d}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor="schedule-hour">Execution Time (UTC)</FieldLabel>
            <NativeSelect
              id="schedule-hour"
              wrapperClassName="w-full"
              value={hour}
              onChange={(e) => {
                setHour(Number(e.target.value));
              }}>
              {HOURS.map((h) => (
                <NativeSelectOption key={h.value} value={h.value}>
                  {h.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        </div>
      ) : null}

      {preset === 'custom' ? (
        <Field>
          <FieldLabel htmlFor="schedule-cron">Cron Expression</FieldLabel>
          <Input
            id="schedule-cron"
            placeholder="0 9 * * 1 (e.g. Every Mon at 09:00 UTC)"
            value={custom}
            className="font-mono"
            onChange={(e) => {
              setCustom(e.target.value);
            }}
          />
          <FieldDescription className="font-mono">
            ┌ minute &nbsp;┬ hour &nbsp;┬ day-of-month &nbsp;┬ month &nbsp;┬ day-of-week
          </FieldDescription>
        </Field>
      ) : null}

      {compact ? (
        <p className="text-muted-foreground text-xs">
          <span className="text-foreground font-medium">{description}</span>{' '}
          <span className="font-mono">{cron ?? '—'}</span>
        </p>
      ) : (
        <Alert>
          <Clock />
          <AlertTitle>{description}</AlertTitle>
          <AlertDescription className="font-mono">{cron ?? '—'}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
